import { useCallback, useMemo, useState } from 'react';

import { useSearchParams } from 'react-router-dom';

import { DateTime } from 'luxon';

import { CreditCounterpartyProps } from '@graphql/query/operationLimits/credit/counterpartList';

import { calculateDefaultCompetenceRange } from '../../hooks/useOperationLimitsCompetenceFilters';
import { NineBoxMetricProps, NineBoxRatingProps, NINEBOX_METRICS, NineBoxYearProps } from '../components/NineBox/mockData';
import { fromCounterpartyRatingFilterValue, toCounterpartyRatingFilterValue } from '../utils/rating';

export interface NineBoxCellSelectionProps {
  rating: NineBoxRatingProps;
  year: NineBoxYearProps;
}

export interface UseOperationLimitsNineBoxProps {
  counterparties: CreditCounterpartyProps[];
}

export interface UseOperationLimitsNineBoxReturnProps {
  metric: NineBoxMetricProps;
  selectedCell?: NineBoxCellSelectionProps;
  selectedRating?: NineBoxRatingProps;
  counterparties: CreditCounterpartyProps[];
  onMetricChange: (metric: NineBoxMetricProps) => void;
  onClearFilters: () => void;
  onRatingSelect: (rating: NineBoxRatingProps) => void;
  onCellSelect: (selection: NineBoxCellSelectionProps) => void;
}

const DEFAULT_METRIC = NINEBOX_METRICS[0];
const MATRIX_YEAR_PARAM = 'matrixYear';

const applyDefaultRange = (searchParams: URLSearchParams) => {
  const defaultRange = calculateDefaultCompetenceRange();
  const startDate = DateTime.fromObject({
    year: defaultRange.startYear,
    month: defaultRange.startMonth,
  });
  const endDate = DateTime.fromObject({
    year: defaultRange.endYear,
    month: defaultRange.endMonth,
  });

  searchParams.set('startCompetence', startDate.toFormat('yyyy-MM'));
  searchParams.set('endCompetence', endDate.toFormat('yyyy-MM'));
  searchParams.delete(MATRIX_YEAR_PARAM);
};

const applyYearRange = (searchParams: URLSearchParams, year: NineBoxYearProps) => {
  const defaultRange = calculateDefaultCompetenceRange();
  const startMonth = year === defaultRange.startYear ? defaultRange.startMonth : 1;
  const startDate = DateTime.fromObject({ year, month: startMonth });
  const endDate = DateTime.fromObject({ year, month: 12 });

  searchParams.set('startCompetence', startDate.toFormat('yyyy-MM'));
  searchParams.set('endCompetence', endDate.toFormat('yyyy-MM'));
  searchParams.set(MATRIX_YEAR_PARAM, String(year));
};

const applyRatingFilter = (searchParams: URLSearchParams, rating: NineBoxRatingProps) => {
  if (rating === 'TOTAL') {
    searchParams.delete('rating');

    return;
  }

  searchParams.set('rating', toCounterpartyRatingFilterValue(rating));
};

function useOperationLimitsNineBox({ counterparties }: UseOperationLimitsNineBoxProps): UseOperationLimitsNineBoxReturnProps {
  const [metric, setMetric] = useState<NineBoxMetricProps>(DEFAULT_METRIC);
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedRating = fromCounterpartyRatingFilterValue(searchParams.get('rating'));
  const selectedYear = searchParams.get(MATRIX_YEAR_PARAM);
  const selectedCell = useMemo<NineBoxCellSelectionProps | undefined>(() => {
    if (!selectedYear) {
      return undefined;
    }

    const year = Number(selectedYear);

    if (!Number.isFinite(year)) {
      return undefined;
    }

    return {
      rating: selectedRating || 'TOTAL',
      year,
    };
  }, [selectedRating, selectedYear]);

  const onCellSelect = useCallback(
    (selection: NineBoxCellSelectionProps) => {
      const nextSearchParams = new URLSearchParams(searchParams);
      const isSelectedCell = selectedCell?.rating === selection.rating && selectedCell?.year === selection.year;

      if (isSelectedCell) {
        nextSearchParams.delete('rating');
        applyDefaultRange(nextSearchParams);
        setSearchParams(nextSearchParams, { replace: true });

        return;
      }

      applyYearRange(nextSearchParams, selection.year);
      applyRatingFilter(nextSearchParams, selection.rating);
      setSearchParams(nextSearchParams, { replace: true });
    },
    [searchParams, selectedCell, setSearchParams],
  );

  const onClearFilters = useCallback(() => {
    const nextSearchParams = new URLSearchParams(searchParams);

    nextSearchParams.delete('rating');
    nextSearchParams.delete(MATRIX_YEAR_PARAM);
    applyDefaultRange(nextSearchParams);
    setSearchParams(nextSearchParams, { replace: true });
  }, [searchParams, setSearchParams]);

  const onRatingSelect = useCallback(
    (rating: NineBoxRatingProps) => {
      const nextSearchParams = new URLSearchParams(searchParams);
      const isSameRating = selectedRating === rating;
      const hasSelectedCell = Boolean(selectedCell);

      nextSearchParams.delete(MATRIX_YEAR_PARAM);

      if (rating === 'TOTAL') {
        nextSearchParams.delete('rating');
        applyDefaultRange(nextSearchParams);
        setSearchParams(nextSearchParams, { replace: true });

        return;
      }

      if (hasSelectedCell) {
        applyDefaultRange(nextSearchParams);
        applyRatingFilter(nextSearchParams, rating);
        setSearchParams(nextSearchParams, { replace: true });

        return;
      }

      if (isSameRating) {
        nextSearchParams.delete('rating');
        applyDefaultRange(nextSearchParams);
        setSearchParams(nextSearchParams, { replace: true });

        return;
      }

      applyRatingFilter(nextSearchParams, rating);
      setSearchParams(nextSearchParams, { replace: true });
    },
    [searchParams, selectedCell, selectedRating, setSearchParams],
  );

  return {
    metric,
    selectedCell,
    selectedRating,
    counterparties,
    onMetricChange: setMetric,
    onClearFilters,
    onRatingSelect,
    onCellSelect,
  };
}

export default useOperationLimitsNineBox;
